import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Select } from './Select'

interface CursorPagerProps {
  /** 1-based page number. */
  page: number
  pageSize: number
  pageSizes: readonly number[]
  /** Rows on the page being shown — the last page is usually short. */
  rowCount: number
  /** True while the feed reports another page after this one. */
  hasNext: boolean
  isFetching?: boolean
  onPrev: () => void
  onNext: () => void
  onPageSizeChange: (size: number) => void
  className?: string
}

/**
 * Page controls for a **cursor-paged** feed.
 *
 * Separate from `Pagination` because the two cannot be the same control. That
 * one is built on `{ total, page, page_size }`: it knows how many pages exist,
 * so it can number them and let you jump. A cursor feed knows neither — the
 * response carries only "here is a token for the next page" — so numbered
 * buttons would have to invent a page count, and a jump to page 7 has no cursor
 * to jump with.
 *
 * What it can honestly offer is a walk: forward while a cursor exists, back
 * through the cursors already seen. It therefore says "Showing 11–20" and not
 * "of 340", because the feed never says how many there are.
 */
export function CursorPager({
  page,
  pageSize,
  pageSizes,
  rowCount,
  hasNext,
  isFetching,
  onPrev,
  onNext,
  onPageSizeChange,
  className,
}: CursorPagerProps) {
  // Only the last page is short, so counting forward from the page number is
  // exact even without a total.
  const from = (page - 1) * pageSize + 1
  const to = from + rowCount - 1

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-3', className)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="text-fg-subtle flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] uppercase">
          Rows
          <Select
            value={String(pageSize)}
            onChange={(next) => onPageSizeChange(Number(next))}
            aria-label="Rows per page"
            className="h-8 w-auto pr-9 pl-2.5 font-mono text-[11px]"
          >
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </label>

        <span className="text-fg-subtle flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] uppercase">
          {rowCount === 0 ? 'No rows' : `Showing ${from}–${to}`}
          {isFetching && <Loader2 aria-hidden className="size-3.5 animate-spin" />}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Step
          label="Previous page"
          disabled={page <= 1 || Boolean(isFetching)}
          onClick={onPrev}
          icon={<ChevronLeft className="size-4" />}
        />
        {/* The page number, not a set of buttons: there is nothing to jump to. */}
        <span className="text-fg-muted grid h-8 min-w-8 place-items-center px-2 font-mono text-xs">
          {page}
        </span>
        <Step
          label="Next page"
          disabled={!hasNext || Boolean(isFetching)}
          onClick={onNext}
          icon={<ChevronRight className="size-4" />}
        />
      </div>
    </div>
  )
}

function Step({
  label,
  disabled,
  onClick,
  icon,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  icon: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="border-line text-fg-muted hover:border-line-bright hover:text-fg grid size-8 place-items-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
    >
      {icon}
    </button>
  )
}
